import {
  buildResolvedVersionEnrichContext,
  getResolvedBuildingVersion,
} from '@shared/helpers/actions/resolved-version.helpers';
import { getElementById } from '@shared/helpers/recursive-element.helpers';
import type { Baseline, OneOfElements } from '@shared/models/project.model';
import { useSelectedVersion } from '@client/store/ui';
import { useSelectedProposalId } from './use-selected-proposal-id.hooks';
import { useMemo } from 'react';
import { ItemOrItemId } from '@shared/models/type-helpers.model';
import { getId } from '@shared/helpers/object.helpers';
import { useDataTables } from '@features/data-table/store/data-table-store.hooks';
import { useProject } from '@client/store/project';
import { usePvgisEnvironmentHourData } from '@client/store/pvgis/pvgis.hook';
import { getEmptyFrozenArray } from '@shared/helpers/array.helpers';
import { getBuildingDesignFromProjectOrBuilding } from '@shared/helpers/design/design.helpers';
import { getProjectMeta } from '@shared/helpers/project.helpers';

/**
 * Current selected building version with the active proposal's changes applied when {@link useSelectedProposalId} is set;
 * otherwise the baseline version unchanged.
 */
export const useResolvedVersion = (): Baseline | undefined => {
  const version = useSelectedVersion();
  const proposalId = useSelectedProposalId();
  const project = useProject();
  const environmentHourData = usePvgisEnvironmentHourData();
  const dataTables = useDataTables();

  const projectId = project?.id;
  const projectMeta = project ? getProjectMeta(project) : undefined;
  const projectDesign = project
    ? getBuildingDesignFromProjectOrBuilding(project)
    : undefined;
  return useMemo(() => {
    if (!version) return undefined;
    return getResolvedBuildingVersion(
      version,
      proposalId,
      buildResolvedVersionEnrichContext(project, {
        weatherHours: environmentHourData ?? getEmptyFrozenArray(),
        dataTables,
      }),
    );
    // `project` read from closure; invalidation follows meta/design (same as enrich cache deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- project ref omitted to avoid store churn
  }, [
    version,
    proposalId,
    projectId,
    projectMeta,
    projectDesign,
    environmentHourData,
    dataTables,
  ]);
};

/**
 * Element from {@link useResolvedVersion} by id, for record-mode UI that must reflect proposal changes.
 * @param elementOrId - Element or element id
 */
export const useResolvedElement = (
  elementId: ItemOrItemId<OneOfElements> | undefined,
): OneOfElements | undefined => {
  const id = elementId ? getId(elementId) : undefined;
  const version = useResolvedVersion();
  return useMemo(
    () => (id ? getElementById(version, id) : undefined),
    [version, id],
  );
};
